import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as M from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { SchoolV1 } from 'types';
import getTimezone from 'utils/getTimezone';
import {
  coordinateNumber,
  haveSameUpcomingClockTimes,
  isValidCoordinates,
  isValidTimezone,
} from 'utils/timezones';

interface Props {
  school: SchoolV1;
  onEdit: () => void;
}

export const SchoolTimezoneBadge = ({ school, onEdit }: Props) => {
  const [opened, setOpened] = useState(false);
  const lat = coordinateNumber(school.lat);
  const lng = coordinateNumber(school.lng);
  const hasLocation = isValidCoordinates(lat, lng);
  const validTimezone = isValidTimezone(school.timezone);
  const lookup = useQuery({
    queryKey: ['schoolTimezone', school.school_name, lat, lng],
    queryFn: () => getTimezone(lat, lng),
    enabled: hasLocation,
    staleTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const detected = lookup.data?.id;
  const matches = useMemo(
    () =>
      isValidTimezone(school.timezone) && detected
        ? haveSameUpcomingClockTimes(school.timezone, detected)
        : undefined,
    [school.timezone, detected],
  );

  const invalid = !validTimezone;
  const unverified = !hasLocation || lookup.isError;
  if (!invalid && !unverified && matches !== false) return null;

  const label = invalid
    ? school.timezone
      ? 'Invalid timezone'
      : 'Timezone missing'
    : unverified
    ? 'Timezone unverified'
    : 'Timezone mismatch';
  const color = invalid || !unverified ? 'red' : 'orange';
  const explanation = invalid
    ? 'Set a valid timezone so schedules use the correct local time.'
    : !hasLocation
    ? 'Add valid school coordinates to check its timezone.'
    : lookup.isError
    ? 'The location lookup failed. The school timezone has not been verified.'
    : 'Schedule times may be shifted, including during daylight-saving changes. Confirm the school coordinates before changing its timezone.';

  return (
    <M.Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withArrow
      shadow="md"
      width={350}
      withinPortal
    >
      <M.Popover.Target>
        <M.Badge
          component="button"
          type="button"
          variant="light"
          color={color}
          size="lg"
          radius="sm"
          leftSection={<IconAlertTriangle size={16} aria-hidden="true" />}
          aria-expanded={opened}
          onClick={() => setOpened((value) => !value)}
          sx={(theme) => ({
            backgroundColor: theme.white,
            color: theme.colors[color][9],
            '&:hover': { backgroundColor: theme.colors[color][0] },
            cursor: 'pointer',
            textTransform: 'none',
            flexShrink: 0,
            height: 'auto',
            padding: '4px 10px',
            border: '1px solid currentColor',
          })}
        >
          {label}
        </M.Badge>
      </M.Popover.Target>
      <M.Popover.Dropdown sx={{ maxWidth: 'calc(100vw - 32px)' }}>
        <M.Stack spacing="sm">
          <M.Text weight={600}>{label}</M.Text>
          <M.Text size="sm" sx={{ overflowWrap: 'anywhere' }}>
            Stored: {school.timezone || 'Not set'}
          </M.Text>
          {detected && (
            <M.Text size="sm" sx={{ overflowWrap: 'anywhere' }}>
              At this location: {detected}
            </M.Text>
          )}
          <M.Text size="sm">{explanation}</M.Text>
          <M.Group spacing="xs">
            <M.Button
              size="xs"
              onClick={() => {
                setOpened(false);
                onEdit();
              }}
            >
              Edit school
            </M.Button>
            {hasLocation && lookup.isError && (
              <M.Button
                size="xs"
                variant="subtle"
                loading={lookup.isFetching}
                onClick={() => {
                  void lookup.refetch();
                }}
              >
                Retry check
              </M.Button>
            )}
          </M.Group>
        </M.Stack>
      </M.Popover.Dropdown>
    </M.Popover>
  );
};
